import { useState, useEffect, useCallback, useRef } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import { API_URL } from "../config/api";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Building2,
  FileText,
  Phone,
  Mail,
  Upload,
  Download,
  Search,
  Plus,
  RefreshCw,
  AlertTriangle,
  Clock,
  DollarSign,
  TrendingUp,
  Users,
  ChevronRight,
  ChevronDown,
  Edit,
  Trash2,
  MoreVertical,
  CheckCircle,
  XCircle,
  Ban,
  MessageSquare,
  Calendar,
  ArrowUpDown,
  Filter,
  Star,
  Flag,
  Handshake,
  Shield,
  Activity,
  FileSpreadsheet,
  GripVertical,
  ExternalLink,
  PhoneCall,
  Video,
  User,
  Send,
  FileCheck,
  AlertOctagon,
  Landmark,
  CreditCard,
  UserPlus,
  EyeOff,
  Eye,
  Percent,
  Loader2,
} from "lucide-react";


// Category options
const VENDOR_CATEGORIES = [
  { value: "transport", label: "Transport" },
  { value: "shipping", label: "Shipping Line" },
  { value: "customs", label: "Customs" },
  { value: "packaging", label: "Packaging" },
  { value: "maintenance", label: "Maintenance" },
  { value: "warehouse", label: "Warehouse" },
  { value: "insurance", label: "Insurance" },
  { value: "other", label: "Other" },
];

// Status colors
const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-800",
  approved: "bg-green-100 text-green-800",
  on_hold: "bg-yellow-100 text-yellow-800",
  disputed: "bg-red-100 text-red-800",
  paid: "bg-slate-100 text-slate-800",
  partial: "bg-orange-100 text-orange-800",
};

// Aging bucket colors
const AGING_COLORS = {
  not_due: "bg-green-100 text-green-800",
  "0-30": "bg-yellow-100 text-yellow-800",
  "31-60": "bg-orange-100 text-orange-800",
  "61-90": "bg-red-100 text-red-800",
  "90+": "bg-red-200 text-red-900",
};

// Badge colors
const BADGE_COLORS = {
  PAST_DUE: "bg-red-500 text-white",
  CRITICAL: "bg-purple-500 text-white",
  HIGH_PRESSURE: "bg-orange-500 text-white",
  DISCOUNT_EXPIRING: "bg-blue-500 text-white",
};

// Cash Flow Exclusion Reasons
const EXCLUSION_REASONS = [
  { value: "credit_note", label: "Credit note expected" },
  { value: "disputed", label: "Disputed / under claim" },
  { value: "netting", label: "Netting / offset against AR/AP" },
  { value: "duplicate", label: "Duplicate / incorrect" },
  { value: "waived", label: "Waived / forgiven" },
  { value: "reclass", label: "Reclass / non-cash" },
];

// Contact channel icons
const CHANNEL_ICONS = {
  call: PhoneCall,
  email: Mail,
  sms: MessageSquare,
  whatsapp: MessageSquare,
  visit: User,
  other: Activity,
};

export default function VendorAPPage() {
  const { token, user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);

  // Dashboard data
  const [dashboard, setDashboard] = useState(null);

  // Vendors
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({
    name: "",
    category: "other",
    is_critical: false,
    substitutability: 1,
    alt_vendor_count: 0,
    payment_terms_days: 30,
    early_pay_discount_percent: null,
    early_pay_discount_days: null,
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    vat_number: "",
    address: "",
    notes: "",
  });
  const [vendorFilter, setVendorFilter] = useState({ category: "", search: "" });

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    vendor_id: "",
    invoice_number: "",
    amount: 0,
    currency: "EUR",
    invoice_date: "",
    due_date: "",
    status: "open",
    description: "",
  });

  // Contact Events
  const [contactEvents, setContactEvents] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    vendor_id: "",
    vendor_name: "",  // For free text vendor name
    pic_name: "",     // Person In Charge name
    pic_email: "",    // Person In Charge email
    pic_phone: "",    // Person In Charge phone
    channel: "call",
    direction: "inbound",
    topic: "payment_request",
    urgency: "medium",
    notes: "",
  });

  // Vendor search state for combobox
  const [vendorSearchOpen, setVendorSearchOpen] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState("");
  const [selectedVendorContacts, setSelectedVendorContacts] = useState([]);

  // Priority Lists
  const [payNowList, setPayNowList] = useState([]);
  const [highPressureList, setHighPressureList] = useState([]);
  const [negotiationList, setNegotiationList] = useState([]);

  // GRN Customs Payments
  const [grnPayments, setGrnPayments] = useState([]);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [showGRNModal, setShowGRNModal] = useState(false);

  // Payment Drafts
  const [paymentDrafts, setPaymentDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [selectedInvoicesForDraft, setSelectedInvoicesForDraft] = useState([]);
  
  // Selection state for push-to-draft
  const [selectedGRNs, setSelectedGRNs] = useState([]);
  const [selectedForPayment, setSelectedForPayment] = useState([]);
  
  // Immediate Payment Modal
  const [showImmediatePaymentModal, setShowImmediatePaymentModal] = useState(false);
  const [immediatePaymentForm, setImmediatePaymentForm] = useState({
    vendor_id: "",
    vendor_name: "",
    amount: 0,
    payment_type: "partial",
    notes: "",
    total_soa: 0
  });
  const [vendorSOA, setVendorSOA] = useState(null);
  
  // Board Members Management
  const [boardMembers, setBoardMembers] = useState([]);
  const [showBoardMemberModal, setShowBoardMemberModal] = useState(false);
  const [boardMemberForm, setBoardMemberForm] = useState({
    name: "",
    email: "",
    phone: "",
    whatsapp: "",
    role: "Board Member",
    is_active: true
  });

  // Cash Flow Exclusion
  const [showExclusionModal, setShowExclusionModal] = useState(false);
  const [exclusionForm, setExclusionForm] = useState({
    invoice_id: "",
    invoice_number: "",
    vendor_name: "",
    amount: 0,
    include_in_cashflow: true,
    cashflow_exclusion_reason: "",
    cashflow_exclusion_comment: "",
    expected_resolution_date: "",
    exclusion_probability: 100,
    adjusted_cash_amount: null,
    use_partial_exclusion: false
  });
  const [cashflowExclusions, setCashflowExclusions] = useState([]);
  const [showExclusionsPanel, setShowExclusionsPanel] = useState(false);

  // Contact Event Email Drag/Drop
  const [isDraggingContact, setIsDraggingContact] = useState(false);
  const [isProcessingEmail, setIsProcessingEmail] = useState(false);

  // Drag & drop ref
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch functions
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    }
  }, [token]);

  const fetchVendors = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (vendorFilter.category) params.append("category", vendorFilter.category);
      if (vendorFilter.search) params.append("search", vendorFilter.search);

      const response = await axios.get(`${API_URL}/vendor-ap/vendors?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(response.data.vendors);
    } catch (error) {
      toast.error("Failed to fetch vendors");
    }
  }, [token, vendorFilter]);

  const fetchInvoices = useCallback(async (vendorId = null) => {
    try {
      const params = new URLSearchParams();
      if (vendorId) params.append("vendor_id", vendorId);

      const response = await axios.get(`${API_URL}/vendor-ap/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data.invoices);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    }
  }, [token]);

  const fetchContactEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/contact-events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContactEvents(response.data.events);
    } catch (error) {
      console.error("Failed to fetch contact events:", error);
    }
  }, [token]);

  const fetchPayNowList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/lists/pay-now`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayNowList(response.data.invoices);
    } catch (error) {
      console.error("Failed to fetch pay-now list:", error);
    }
  }, [token]);

  const fetchHighPressureList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/lists/high-pressure`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHighPressureList(response.data.vendors);
    } catch (error) {
      console.error("Failed to fetch high-pressure list:", error);
    }
  }, [token]);

  const fetchNegotiationList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/lists/negotiation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNegotiationList(response.data.vendors);
    } catch (error) {
      console.error("Failed to fetch negotiation list:", error);
    }
  }, [token]);

  const fetchGRNPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/grn-payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGrnPayments(response.data.payments);
    } catch (error) {
      console.error("Failed to fetch GRN payments:", error);
    }
  }, [token]);

  const fetchPaymentDrafts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/payment-drafts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaymentDrafts(response.data.drafts);
    } catch (error) {
      console.error("Failed to fetch payment drafts:", error);
    }
  }, [token]);

  // Cash Flow Exclusion fetch (must be before useEffect)
  const fetchCashflowExclusions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/cashflow-exclusions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCashflowExclusions(response.data);
    } catch (error) {
      console.error("Failed to fetch exclusions:", error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    fetchDashboard();
    fetchVendors();
    fetchInvoices();
    fetchContactEvents();
    fetchPayNowList();
    fetchHighPressureList();
    fetchNegotiationList();
    fetchGRNPayments();
    fetchPaymentDrafts();
    fetchBoardMembers();
    fetchCashflowExclusions();
  }, [fetchDashboard, fetchVendors, fetchInvoices, fetchContactEvents, fetchPayNowList, fetchHighPressureList, fetchNegotiationList, fetchGRNPayments, fetchPaymentDrafts, fetchCashflowExclusions]);

  // Handlers
  const handleCreateVendor = async () => {
    if (!vendorForm.name) {
      toast.error("Vendor name is required");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/vendor-ap/vendors`, vendorForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Vendor created successfully");
      setShowVendorModal(false);
      setVendorForm({
        name: "",
        category: "other",
        is_critical: false,
        substitutability: 1,
        alt_vendor_count: 0,
        payment_terms_days: 30,
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        vat_number: "",
        address: "",
        notes: "",
      });
      fetchVendors();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create vendor");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.vendor_id || !invoiceForm.invoice_number || !invoiceForm.amount) {
      toast.error("Vendor, invoice number, and amount are required");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/vendor-ap/invoices`, {
        ...invoiceForm,
        invoice_date: new Date(invoiceForm.invoice_date).toISOString(),
        due_date: new Date(invoiceForm.due_date).toISOString(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Invoice created successfully");
      setShowInvoiceModal(false);
      setInvoiceForm({
        vendor_id: "",
        invoice_number: "",
        amount: 0,
        currency: "EUR",
        invoice_date: "",
        due_date: "",
        status: "open",
        description: "",
      });
      fetchInvoices();
      fetchDashboard();
      fetchPayNowList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContactEvent = async () => {
    if (!contactForm.vendor_id && !contactForm.vendor_name) {
      toast.error("Please select or enter a vendor");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/vendor-ap/contact-events`, {
        ...contactForm,
        // If vendor_id is empty but vendor_name has value, it's a free-text entry
        vendor_name: contactForm.vendor_id ? null : contactForm.vendor_name,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Contact event logged");
      setShowContactModal(false);
      setContactForm({
        vendor_id: "",
        vendor_name: "",
        pic_name: "",
        pic_email: "",
        pic_phone: "",
        channel: "call",
        direction: "inbound",
        topic: "payment_request",
        urgency: "medium",
        notes: "",
      });
      setSelectedVendorContacts([]);
      setVendorSearchQuery("");
      fetchContactEvents();
      fetchHighPressureList();
      fetchPayNowList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log contact event");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await axios.post(`${API_URL}/vendor-ap/invoices/${invoiceId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Invoice status updated to ${newStatus}`);
      fetchInvoices();
      fetchPayNowList();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to update invoice status");
    }
  };

  // Cash Flow Exclusion Modal Handlers
  const openExclusionModal = (invoice, isReinclude = false) => {
    setExclusionForm({
      invoice_id: invoice.id,
      invoice_number: invoice.invoice_number,
      vendor_name: invoice.vendor_name,
      amount: invoice.amount_eur || invoice.amount,
      include_in_cashflow: isReinclude,
      cashflow_exclusion_reason: invoice.cashflow_exclusion_reason || "",
      cashflow_exclusion_comment: invoice.cashflow_exclusion_comment || "",
      expected_resolution_date: invoice.expected_resolution_date ? invoice.expected_resolution_date.split("T")[0] : "",
      exclusion_probability: invoice.exclusion_probability || 100,
      adjusted_cash_amount: invoice.adjusted_cash_amount || null,
      use_partial_exclusion: invoice.adjusted_cash_amount !== null && invoice.adjusted_cash_amount !== undefined
    });
    setShowExclusionModal(true);
  };

  const handleSaveExclusion = async () => {
    if (!exclusionForm.include_in_cashflow) {
      if (!exclusionForm.cashflow_exclusion_reason) {
        toast.error("Please select a reason for excluding from cash flow");
        return;
      }
      if (!exclusionForm.cashflow_exclusion_comment) {
        toast.error("Please provide a comment explaining the exclusion");
        return;
      }
    }

    setLoading(true);
    try {
      await axios.patch(
        `${API_URL}/vendor-ap/invoices/${exclusionForm.invoice_id}/cashflow-exclusion?user_id=${user?.id || "system"}`,
        {
          include_in_cashflow: exclusionForm.include_in_cashflow,
          cashflow_exclusion_reason: exclusionForm.cashflow_exclusion_reason || null,
          cashflow_exclusion_comment: exclusionForm.cashflow_exclusion_comment || null,
          expected_resolution_date: exclusionForm.expected_resolution_date ? new Date(exclusionForm.expected_resolution_date).toISOString() : null,
          exclusion_probability: exclusionForm.exclusion_probability,
          adjusted_cash_amount: exclusionForm.use_partial_exclusion ? exclusionForm.adjusted_cash_amount : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(exclusionForm.include_in_cashflow 
        ? "Invoice re-included in cash flow forecast" 
        : "Invoice excluded from cash flow forecast"
      );
      setShowExclusionModal(false);
      fetchInvoices();
      fetchCashflowExclusions();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update exclusion");
    } finally {
      setLoading(false);
    }
  };

  const handleReconfirmExclusion = async (invoiceId, newDate = null) => {
    try {
      await axios.post(
        `${API_URL}/vendor-ap/invoices/${invoiceId}/cashflow-exclusion/reconfirm?user_id=${user?.id || "system"}`,
        { new_resolution_date: newDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Exclusion reconfirmed");
      fetchCashflowExclusions();
    } catch (error) {
      toast.error("Failed to reconfirm exclusion");
    }
  };

  const handleImportFile = async (type, file) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const endpoint = type === "vendors" ? "vendors/import" : "invoices/import";
      const response = await axios.post(`${API_URL}/vendor-ap/${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(`Imported ${response.data.imported} ${type}`);
      if (response.data.errors?.length > 0) {
        console.warn("Import errors:", response.data.errors);
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      fetchVendors();
      fetchInvoices();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  // Push to Draft handler
  const handlePushToDraft = async (sourceType, sourceIds) => {
    if (sourceIds.length === 0) {
      toast.error("Please select items to push to draft");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/vendor-ap/push-to-draft`, {
        source_type: sourceType,
        source_ids: sourceIds,
        notes: ""
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Created draft with ${response.data.draft.items.length} items - Total: €${response.data.total_amount.toLocaleString()}`);
      
      if (response.data.requires_approval) {
        toast.info("⚠️ This payment exceeds €10,000 and will require board approval");
      }
      
      // Clear selections
      setSelectedGRNs([]);
      setSelectedForPayment([]);
      
      // Switch to drafts tab
      setActiveTab("drafts");
      fetchPaymentDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment draft");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Vendor SOA
  const fetchVendorSOA = async (vendorId) => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/vendor-soa/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorSOA(response.data);
      setImmediatePaymentForm(prev => ({
        ...prev,
        vendor_id: vendorId,
        vendor_name: response.data.vendor_name,
        total_soa: response.data.total_soa
      }));
    } catch (error) {
      console.error("Failed to fetch vendor SOA:", error);
    }
  };

  // Fetch Board Members
  const fetchBoardMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/board-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoardMembers(response.data.members || []);
    } catch (error) {
      console.error("Failed to fetch board members:", error);
    }
  };

  // Add Board Member
  const handleAddBoardMember = async () => {
    try {
      await axios.post(`${API_URL}/vendor-ap/board-members`, boardMemberForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Board member added");
      setShowBoardMemberModal(false);
      setBoardMemberForm({ name: "", email: "", phone: "", whatsapp: "", role: "Board Member", is_active: true });
      fetchBoardMembers();
    } catch (error) {
      toast.error("Failed to add board member");
    }
  };

  // Toggle Board Member Active Status
  const toggleBoardMemberActive = async (memberId, currentStatus) => {
    try {
      await axios.patch(`${API_URL}/vendor-ap/board-members/${memberId}`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Board member ${!currentStatus ? "activated" : "deactivated"}`);
      fetchBoardMembers();
    } catch (error) {
      toast.error("Failed to update board member");
    }
  };

  // Delete Board Member
  const handleDeleteBoardMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this board member?")) return;
    try {
      await axios.delete(`${API_URL}/vendor-ap/board-members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Board member removed");
      fetchBoardMembers();
    } catch (error) {
      toast.error("Failed to remove board member");
    }
  };

  // Create Immediate Payment
  const handleCreateImmediatePayment = async () => {
    if (!immediatePaymentForm.vendor_name || !immediatePaymentForm.amount) {
      toast.error("Vendor and amount are required");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/vendor-ap/immediate-payment`, immediatePaymentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Now submit it
      const submitResponse = await axios.post(
        `${API_URL}/vendor-ap/immediate-payment/${response.data.payment.id}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (submitResponse.data.status === "pending_approval") {
        toast.info("⚠️ Payment exceeds €10,000 - Board approval required");
      } else {
        toast.success(`Payment request sent to ${submitResponse.data.email_to || 'accounting'}`);
      }
      
      setShowImmediatePaymentModal(false);
      setImmediatePaymentForm({
        vendor_id: "",
        vendor_name: "",
        amount: 0,
        payment_type: "partial",
        notes: "",
        total_soa: 0
      });
      setVendorSOA(null);
      fetchPaymentDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setLoading(false);
    }
  };

  // Drag & drop for email upload
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const emailFiles = files.filter(f => f.name.endsWith('.eml') || f.name.endsWith('.msg') || f.type === 'message/rfc822');

    if (emailFiles.length === 0) {
      toast.error("Please drop an email file (.eml or .msg)");
      return;
    }

    // Need to select vendor first
    if (!contactForm.vendor_id) {
      toast.error("Please select a vendor first before dropping an email");
      return;
    }

    const formData = new FormData();
    formData.append("file", emailFiles[0]);
    formData.append("vendor_id", contactForm.vendor_id);
    formData.append("topic", "payment_request");
    formData.append("urgency", "medium");
    formData.append("handled_by", user?.full_name || "");

    try {
      await axios.post(`${API_URL}/vendor-ap/contact-events/email-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Email logged as contact event");
      fetchContactEvents();
      fetchHighPressureList();
    } catch (error) {
      toast.error("Failed to process email");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Contact Event Email Drop Handler
  const handleContactEmailDrop = async (e) => {
    e.preventDefault();
    setIsDraggingContact(false);

    const files = Array.from(e.dataTransfer.files);
    const emailFile = files.find(f => 
      f.name.endsWith('.eml') || 
      f.name.endsWith('.msg') || 
      f.type === 'message/rfc822'
    );

    if (!emailFile) {
      toast.error("Please drop an email file (.eml or .msg)");
      return;
    }

    setIsProcessingEmail(true);
    const formData = new FormData();
    formData.append("file", emailFile);

    try {
      const response = await axios.post(`${API_URL}/vendor-ap/parse-contact-email`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      const { sender, subject, date, body_preview, matched_vendor } = response.data;
      
      // Auto-fill the contact form
      setContactForm(prev => ({
        ...prev,
        vendor_id: matched_vendor?.id || prev.vendor_id,
        channel: "email",
        direction: "inbound",
        topic: detectTopic(subject, body_preview),
        urgency: detectUrgency(subject, body_preview),
        notes: `Subject: ${subject}\n\nFrom: ${sender}\nDate: ${date}\n\n${body_preview || ""}`,
        email_subject: subject,
        email_from: sender,
        email_date: date
      }));
      
      if (matched_vendor) {
        toast.success(`Email parsed - matched vendor: ${matched_vendor.name}`);
      } else {
        toast.success("Email parsed - please select vendor manually");
      }
    } catch (error) {
      toast.error("Failed to parse email");
      console.error(error);
    } finally {
      setIsProcessingEmail(false);
    }
  };

  // Helper to detect topic from email content
  const detectTopic = (subject, body) => {
    const text = `${subject} ${body}`.toLowerCase();
    if (text.includes("payment") || text.includes("invoice") || text.includes("due") || text.includes("overdue")) {
      return "payment_request";
    }
    if (text.includes("reminder") || text.includes("outstanding")) {
      return "reminder";
    }
    if (text.includes("dispute") || text.includes("claim") || text.includes("issue")) {
      return "dispute";
    }
    return "inquiry";
  };

  // Helper to detect urgency from email content
  const detectUrgency = (subject, body) => {
    const text = `${subject} ${body}`.toLowerCase();
    if (text.includes("urgent") || text.includes("asap") || text.includes("immediate") || text.includes("critical")) {
      return "high";
    }
    if (text.includes("overdue") || text.includes("past due") || text.includes("final notice")) {
      return "high";
    }
    return "medium";
  };

  // Format currency
  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency,
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="vendor-ap-title">
                <DollarSign className="h-6 w-6" />
                Vendor / AP Intelligence
              </h1>
              <p className="text-blue-200 text-sm mt-1">
                Manage payables, track vendor pressure, prioritize payments
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowContactModal(true)}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="log-contact-btn"
              >
                <Phone className="h-4 w-4 mr-2" />
                Log Contact
              </Button>
              {isAdmin && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowVendorModal(true)}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    data-testid="add-vendor-btn"
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Add Vendor
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowInvoiceModal(true)}
                    className="bg-white text-[#003366] hover:bg-blue-50"
                    data-testid="add-invoice-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Invoice
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
              <Activity className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="pay-now" className="gap-2" data-testid="tab-pay-now">
              <CheckCircle className="h-4 w-4" />
              Pay Now
            </TabsTrigger>
            <TabsTrigger value="pressure" className="gap-2" data-testid="tab-pressure">
              <AlertTriangle className="h-4 w-4" />
              High Pressure
            </TabsTrigger>
            <TabsTrigger value="negotiate" className="gap-2" data-testid="tab-negotiate">
              <Handshake className="h-4 w-4" />
              Negotiation List
            </TabsTrigger>
            <TabsTrigger value="vendors" className="gap-2" data-testid="tab-vendors">
              <Building2 className="h-4 w-4" />
              Vendors
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2" data-testid="tab-invoices">
              <FileText className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="grn" className="gap-2" data-testid="tab-grn">
              <Landmark className="h-4 w-4" />
              GRN Customs
            </TabsTrigger>
            <TabsTrigger value="drafts" className="gap-2" data-testid="tab-drafts">
              <Send className="h-4 w-4" />
              Payment Drafts
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-2" data-testid="tab-contacts">
              <Phone className="h-4 w-4" />
              Contact Log
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboard && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Total Open Payables</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-700" data-testid="total-open-amount">
                        {formatCurrency(dashboard.summary.total_open_amount)}
                      </div>
                      <p className="text-sm text-slate-500">{dashboard.summary.total_open_invoices} invoices</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Past Due</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-600">
                        {formatCurrency(
                          (dashboard.aging_summary["0-30"] || 0) +
                          (dashboard.aging_summary["31-60"] || 0) +
                          (dashboard.aging_summary["61-90"] || 0) +
                          (dashboard.aging_summary["90+"] || 0)
                        )}
                      </div>
                      <p className="text-sm text-slate-500">Overdue amount</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Total Vendors</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{dashboard.summary.total_vendors}</div>
                      <p className="text-sm text-slate-500">{dashboard.summary.critical_vendors} critical</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">High Pressure Vendors</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-orange-600">{dashboard.high_pressure_vendors?.length || 0}</div>
                      <p className="text-sm text-slate-500">Vendors pushing for payment</p>
                    </CardContent>
                  </Card>

                  {/* Excluded Liabilities Card */}
                  <Card className="border-purple-200 bg-purple-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
                        <EyeOff className="h-4 w-4" />
                        Excluded from Cash Flow
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-purple-600">
                        {formatCurrency(cashflowExclusions?.summary?.total_excluded_amount || 0)}
                      </div>
                      <p className="text-sm text-purple-600">{cashflowExclusions?.summary?.total_count || 0} items pending write-off</p>
                      {cashflowExclusions?.summary?.total_count > 0 && (
                        <Button 
                          variant="link" 
                          className="text-purple-700 p-0 h-auto mt-1"
                          onClick={() => setShowExclusionsPanel(true)}
                        >
                          View excluded items →
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Aging Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Aging Summary</CardTitle>
                    <CardDescription>Open payables by aging bucket</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-4">
                      {[
                        { key: "not_due", label: "Not Due", color: "bg-green-500" },
                        { key: "0-30", label: "0-30 Days", color: "bg-yellow-500" },
                        { key: "31-60", label: "31-60 Days", color: "bg-orange-500" },
                        { key: "61-90", label: "61-90 Days", color: "bg-red-500" },
                        { key: "90+", label: "90+ Days", color: "bg-red-700" },
                      ].map((bucket) => (
                        <div key={bucket.key} className="text-center">
                          <div className={`h-24 ${bucket.color} rounded-lg flex items-end justify-center p-2 relative overflow-hidden`}>
                            <div
                              className="bg-white/30 absolute bottom-0 left-0 right-0"
                              style={{
                                height: `${Math.min(100, ((dashboard.aging_summary[bucket.key] || 0) / Math.max(1, dashboard.summary.total_open_amount)) * 100)}%`,
                              }}
                            />
                            <span className="text-white font-bold text-lg relative z-10">
                              {formatCurrency(dashboard.aging_summary[bucket.key] || 0)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-2">{bucket.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Lists */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* High Pressure Vendors */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                        High Pressure Vendors
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.high_pressure_vendors?.length === 0 ? (
                        <p className="text-slate-500 text-sm">No high pressure vendors</p>
                      ) : (
                        <div className="space-y-2">
                          {dashboard.high_pressure_vendors?.map((vendor) => (
                            <div key={vendor.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="font-medium">{vendor.name}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-orange-100 text-orange-700">
                                  {vendor.contacts_7d} contacts (7d)
                                </Badge>
                                <Badge>{Math.round(vendor.pressure_score)} pts</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Critical Vendors with Balance */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-purple-500" />
                        Critical Vendors Outstanding
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.critical_vendors_with_balance?.length === 0 ? (
                        <p className="text-slate-500 text-sm">No critical vendors with open balance</p>
                      ) : (
                        <div className="space-y-2">
                          {dashboard.critical_vendors_with_balance?.map((vendor) => (
                            <div key={vendor.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="font-medium">{vendor.name}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{vendor.invoice_count} invoices</Badge>
                                <span className="font-bold text-purple-700">{formatCurrency(vendor.open_balance)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Pay Now Tab */}
          <TabsContent value="pay-now">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Pay Now Queue
                    </CardTitle>
                    <CardDescription>
                      Prioritized list of invoices to pay based on aging, criticality, usage, and vendor pressure
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchPayNowList}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {payNowList.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                    <p className="text-slate-500">No invoices requiring immediate attention</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-sm text-slate-500">
                          <th className="pb-2 pr-4">Priority</th>
                          <th className="pb-2 pr-4">Vendor</th>
                          <th className="pb-2 pr-4">Invoice</th>
                          <th className="pb-2 pr-4">Amount</th>
                          <th className="pb-2 pr-4">Due Date</th>
                          <th className="pb-2 pr-4">Days Past Due</th>
                          <th className="pb-2 pr-4">Badges</th>
                          <th className="pb-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payNowList.map((invoice, index) => (
                          <tr key={invoice.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-2">
                                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                                  {index + 1}
                                </span>
                                <span className="text-xs text-slate-500">{invoice.priority_score} pts</span>
                              </div>
                            </td>
                            <td className="py-3 pr-4 font-medium">{invoice.vendor_name}</td>
                            <td className="py-3 pr-4">{invoice.invoice_number}</td>
                            <td className="py-3 pr-4 font-medium">{formatCurrency(invoice.amount_eur)}</td>
                            <td className="py-3 pr-4">{formatDate(invoice.due_date)}</td>
                            <td className="py-3 pr-4">
                              <Badge className={invoice.days_past_due > 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                                {invoice.days_past_due > 0 ? `+${invoice.days_past_due}` : invoice.days_past_due} days
                              </Badge>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex flex-wrap gap-1">
                                {invoice.badges?.map((badge) => (
                                  <Badge key={badge} className={BADGE_COLORS[badge] || "bg-slate-100"}>
                                    {badge.replace("_", " ")}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "approved")}>
                                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                    Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "paid")}>
                                    <DollarSign className="h-4 w-4 mr-2 text-blue-600" />
                                    Mark Paid
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "on_hold")}>
                                    <Clock className="h-4 w-4 mr-2 text-yellow-600" />
                                    Put On Hold
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "disputed")}>
                                    <Ban className="h-4 w-4 mr-2 text-red-600" />
                                    Mark Disputed
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Score Breakdown Legend */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Priority Score Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="font-bold text-blue-700">55%</div>
                    <div className="text-slate-600">Aging Score</div>
                    <div className="text-xs text-slate-500">How overdue is the invoice</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="font-bold text-purple-700">25%</div>
                    <div className="text-slate-600">Criticality</div>
                    <div className="text-xs text-slate-500">Is vendor critical to ops?</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="font-bold text-green-700">15%</div>
                    <div className="text-slate-600">Usage Score</div>
                    <div className="text-xs text-slate-500">How often we use them</div>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <div className="font-bold text-orange-700">5%</div>
                    <div className="text-slate-600">Pressure (capped)</div>
                    <div className="text-xs text-slate-500">Contact frequency</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* High Pressure Tab */}
          <TabsContent value="pressure">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      High Pressure Vendors
                    </CardTitle>
                    <CardDescription>
                      Vendors contacting frequently about payments
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchHighPressureList}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {highPressureList.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                    <p className="text-slate-500">No high pressure vendors at this time</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {highPressureList.map((vendor) => (
                      <div key={vendor.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{vendor.name}</h3>
                            {vendor.is_critical && (
                              <Badge className="bg-purple-100 text-purple-700">CRITICAL</Badge>
                            )}
                            <Badge variant="outline">{vendor.category}</Badge>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-orange-600">{Math.round(vendor.pressure_score)}</div>
                              <div className="text-xs text-slate-500">Pressure Score</div>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4 mt-3">
                          <div>
                            <div className="text-sm text-slate-500">Open Balance</div>
                            <div className="font-semibold">{formatCurrency(vendor.open_balance)}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-500">Contacts (7d)</div>
                            <div className="font-semibold">{vendor.contacts_7d}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-500">Contacts (30d)</div>
                            <div className="font-semibold">{vendor.contacts_30d}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-500">Last Contact</div>
                            <div className="font-semibold">{formatDate(vendor.last_contact_date)}</div>
                          </div>
                        </div>
                        {vendor.contact_breakdown && (
                          <div className="flex gap-2 mt-3">
                            {vendor.contact_breakdown.calls_7d > 0 && (
                              <Badge variant="outline" className="gap-1">
                                <PhoneCall className="h-3 w-3" />
                                {vendor.contact_breakdown.calls_7d} calls
                              </Badge>
                            )}
                            {vendor.contact_breakdown.emails_7d > 0 && (
                              <Badge variant="outline" className="gap-1">
                                <Mail className="h-3 w-3" />
                                {vendor.contact_breakdown.emails_7d} emails
                              </Badge>
                            )}
                            {vendor.contact_breakdown.sms_7d > 0 && (
                              <Badge variant="outline" className="gap-1">
                                <MessageSquare className="h-3 w-3" />
                                {vendor.contact_breakdown.sms_7d} SMS
                              </Badge>
                            )}
                            {vendor.contact_breakdown.visits_7d > 0 && (
                              <Badge variant="outline" className="gap-1">
                                <User className="h-3 w-3" />
                                {vendor.contact_breakdown.visits_7d} visits
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Negotiation List Tab */}
          <TabsContent value="negotiate">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Handshake className="h-5 w-5 text-blue-500" />
                      Negotiation Opportunities
                    </CardTitle>
                    <CardDescription>
                      High pressure vendors that are easy to replace - consider renegotiating terms
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchNegotiationList}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {negotiationList.length === 0 ? (
                  <div className="text-center py-8">
                    <Handshake className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No negotiation opportunities identified</p>
                    <p className="text-xs text-slate-400 mt-1">Vendors with high pressure but easy to replace will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {negotiationList.map((vendor) => (
                      <div key={vendor.id} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{vendor.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{vendor.category}</Badge>
                              <span className="text-sm text-slate-500">
                                {vendor.alt_vendor_count} alternatives available
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-orange-600">Pressure: {Math.round(vendor.pressure_score)}</div>
                            <div className="text-sm text-slate-500">Open: {formatCurrency(vendor.open_balance)}</div>
                          </div>
                        </div>
                        <div className="mt-3 p-2 bg-white rounded border border-blue-100">
                          <div className="flex items-center gap-2 text-blue-700">
                            <Flag className="h-4 w-4" />
                            <span className="text-sm font-medium">{vendor.recommendation}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Vendor Master</CardTitle>
                    <CardDescription>Manage vendor information and settings</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".csv,.xls,.xlsx"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleImportFile("vendors", e.target.files[0])}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          Import CSV/XLS
                        </span>
                      </Button>
                    </label>
                    <Button variant="outline" size="sm" onClick={fetchVendors}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search vendors..."
                        value={vendorFilter.search}
                        onChange={(e) => setVendorFilter((f) => ({ ...f, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select
                    value={vendorFilter.category || "all"}
                    onValueChange={(v) => setVendorFilter((f) => ({ ...f, category: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {VENDOR_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Vendor List */}
                {vendors.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No vendors found</p>
                    <Button onClick={() => setShowVendorModal(true)} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Vendor
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-sm text-slate-500">
                          <th className="pb-2 pr-4">Vendor</th>
                          <th className="pb-2 pr-4">Category</th>
                          <th className="pb-2 pr-4">Critical</th>
                          <th className="pb-2 pr-4">Substitutability</th>
                          <th className="pb-2 pr-4">Payment Terms</th>
                          <th className="pb-2 pr-4">Contact</th>
                          <th className="pb-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendors.map((vendor) => (
                          <tr key={vendor.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedVendor(vendor)}>
                            <td className="py-3 pr-4">
                              <div className="font-medium">{vendor.name}</div>
                              {vendor.vat_number && <div className="text-xs text-slate-500">VAT: {vendor.vat_number}</div>}
                            </td>
                            <td className="py-3 pr-4">
                              <Badge variant="outline">{vendor.category}</Badge>
                            </td>
                            <td className="py-3 pr-4">
                              {vendor.is_critical ? (
                                <Badge className="bg-purple-100 text-purple-700">Yes</Badge>
                              ) : (
                                <span className="text-slate-400">No</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-1">
                                {[0, 1, 2, 3].map((level) => (
                                  <div
                                    key={level}
                                    className={`w-3 h-3 rounded-full ${
                                      level <= vendor.substitutability ? "bg-red-500" : "bg-slate-200"
                                    }`}
                                  />
                                ))}
                                <span className="text-xs text-slate-500 ml-2">
                                  ({vendor.alt_vendor_count} alts)
                                </span>
                              </div>
                            </td>
                            <td className="py-3 pr-4">Net {vendor.payment_terms_days} days</td>
                            <td className="py-3 pr-4">
                              {vendor.contact_name && (
                                <div className="text-sm">
                                  <div>{vendor.contact_name}</div>
                                  <div className="text-slate-500 text-xs">{vendor.contact_email}</div>
                                </div>
                              )}
                            </td>
                            <td className="py-3">
                              <div className="flex flex-wrap gap-1">
                                {vendor.is_blacklisted && (
                                  <Badge className="bg-black text-white">
                                    <Ban className="h-3 w-3 mr-1" />
                                    Blacklisted
                                  </Badge>
                                )}
                                {vendor.block_for_operations && !vendor.is_blacklisted && (
                                  <Badge className="bg-orange-100 text-orange-700">
                                    <Shield className="h-3 w-3 mr-1" />
                                    Ops Blocked
                                  </Badge>
                                )}
                                {vendor.payment_block && (
                                  <Badge className="bg-red-100 text-red-700">
                                    <Ban className="h-3 w-3 mr-1" />
                                    Pay Blocked
                                  </Badge>
                                )}
                                {!vendor.is_blacklisted && !vendor.block_for_operations && !vendor.payment_block && (
                                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Open Payables</CardTitle>
                    <CardDescription>Invoice-level tracking of amounts owed</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".csv,.xls,.xlsx"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleImportFile("invoices", e.target.files[0])}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          Import CSV/XLS
                        </span>
                      </Button>
                    </label>
                    <Button variant="outline" size="sm" onClick={() => fetchInvoices()}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No invoices found</p>
                    <Button onClick={() => setShowInvoiceModal(true)} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Invoice
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-sm text-slate-500">
                          <th className="pb-2 pr-4">Vendor</th>
                          <th className="pb-2 pr-4">Invoice #</th>
                          <th className="pb-2 pr-4">Amount</th>
                          <th className="pb-2 pr-4">Invoice Date</th>
                          <th className="pb-2 pr-4">Due Date</th>
                          <th className="pb-2 pr-4">Aging</th>
                          <th className="pb-2 pr-4">Status</th>
                          <th className="pb-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 pr-4 font-medium">{invoice.vendor_name}</td>
                            <td className="py-3 pr-4">{invoice.invoice_number}</td>
                            <td className="py-3 pr-4 font-medium">{formatCurrency(invoice.amount_eur || invoice.amount, invoice.currency)}</td>
                            <td className="py-3 pr-4">{formatDate(invoice.invoice_date)}</td>
                            <td className="py-3 pr-4">{formatDate(invoice.due_date)}</td>
                            <td className="py-3 pr-4">
                              <Badge className={AGING_COLORS[invoice.aging_bucket] || "bg-slate-100"}>
                                {invoice.aging_bucket}
                              </Badge>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-2">
                                <Badge className={STATUS_COLORS[invoice.status] || "bg-slate-100"}>
                                  {invoice.status}
                                </Badge>
                                {invoice.include_in_cashflow === false && (
                                  <Badge className="bg-purple-100 text-purple-800 text-xs" title={invoice.cashflow_exclusion_comment}>
                                    <EyeOff className="h-3 w-3 mr-1" />
                                    CF Excl.
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "approved")}>
                                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                    Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "paid")}>
                                    <DollarSign className="h-4 w-4 mr-2 text-blue-600" />
                                    Mark Paid
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "on_hold")}>
                                    <Clock className="h-4 w-4 mr-2 text-yellow-600" />
                                    Put On Hold
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateInvoiceStatus(invoice.id, "disputed")}>
                                    <Ban className="h-4 w-4 mr-2 text-red-600" />
                                    Mark Disputed
                                  </DropdownMenuItem>
                                  <div className="border-t my-1" />
                                  {invoice.include_in_cashflow !== false ? (
                                    <DropdownMenuItem onClick={() => openExclusionModal(invoice, false)}>
                                      <EyeOff className="h-4 w-4 mr-2 text-purple-600" />
                                      Exclude from Cash Flow
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => openExclusionModal(invoice, true)}>
                                      <Eye className="h-4 w-4 mr-2 text-green-600" />
                                      Re-include in Cash Flow
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Log Tab */}
          <TabsContent value="contacts">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Email Drop Zone */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-500" />
                    Log Email Contact
                  </CardTitle>
                  <CardDescription>Drag & drop email files here</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Label>Select Vendor First</Label>
                    <Select
                      value={contactForm.vendor_id}
                      onValueChange={(v) => setContactForm((f) => ({ ...f, vendor_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div
                    ref={dropZoneRef}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      isDragging
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-300 hover:border-blue-400"
                    }`}
                  >
                    <Mail className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-sm text-slate-600">
                      Drop .eml or .msg file here
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Email will be logged as a contact event
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Events List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recent Contact Events</CardTitle>
                      <CardDescription>Last 30 days of vendor communications</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchContactEvents}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {contactEvents.length === 0 ? (
                    <div className="text-center py-8">
                      <Phone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No contact events logged yet</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {contactEvents.map((event) => {
                          const ChannelIcon = CHANNEL_ICONS[event.channel] || Activity;
                          return (
                            <div
                              key={event.id}
                              className="p-3 bg-slate-50 rounded-lg border border-slate-100"
                            >
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-full ${
                                  event.direction === "inbound" ? "bg-orange-100" : "bg-blue-100"
                                }`}>
                                  <ChannelIcon className={`h-4 w-4 ${
                                    event.direction === "inbound" ? "text-orange-600" : "text-blue-600"
                                  }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{event.vendor_name}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {event.channel}
                                    </Badge>
                                    <Badge
                                      className={
                                        event.direction === "inbound"
                                          ? "bg-orange-100 text-orange-700"
                                          : "bg-blue-100 text-blue-700"
                                      }
                                    >
                                      {event.direction}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className={
                                        event.urgency === "high"
                                          ? "border-red-300 text-red-700"
                                          : event.urgency === "medium"
                                          ? "border-yellow-300 text-yellow-700"
                                          : "border-green-300 text-green-700"
                                      }
                                    >
                                      {event.urgency}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-slate-600 mt-1">
                                    {event.topic?.replace("_", " ")}
                                    {event.email_subject && `: ${event.email_subject}`}
                                  </p>
                                  {event.notes && (
                                    <p className="text-sm text-slate-500 mt-1">{event.notes}</p>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                                    <span>{formatDate(event.timestamp)}</span>
                                    {event.handled_by && <span>by {event.handled_by}</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* GRN Customs Payments Tab */}
          <TabsContent value="grn">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Landmark className="h-5 w-5 text-red-500" />
                      GRN Customs Payments
                    </CardTitle>
                    <CardDescription>
                      Weekly customs clearance payments - Select items to push to payment draft
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedGRNs.length > 0 && (
                      <Button 
                        onClick={() => handlePushToDraft("grn", selectedGRNs)}
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={loading}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Push {selectedGRNs.length} to Draft
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={fetchGRNPayments}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {grnPayments.length === 0 ? (
                  <div className="text-center py-8">
                    <Landmark className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No GRN payments found</p>
                    <p className="text-xs text-slate-400 mt-1">Import GRN data via Import/Export → GRN Customs Payments</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-sm text-slate-500">
                          <th className="pb-2 pr-2">
                            <input
                              type="checkbox"
                              checked={selectedGRNs.length === grnPayments.filter(p => p.status !== 'paid').length && grnPayments.filter(p => p.status !== 'paid').length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedGRNs(grnPayments.filter(p => p.status !== 'paid').map(p => p.id));
                                } else {
                                  setSelectedGRNs([]);
                                }
                              }}
                              className="rounded"
                            />
                          </th>
                          <th className="pb-2 pr-4">Reference</th>
                          <th className="pb-2 pr-4">Tax Number</th>
                          <th className="pb-2 pr-4">Amount</th>
                          <th className="pb-2 pr-4">Open Amount</th>
                          <th className="pb-2 pr-4">Due Date</th>
                          <th className="pb-2 pr-4">Latest Due</th>
                          <th className="pb-2 pr-4">Days To Due</th>
                          <th className="pb-2 pr-4">Status</th>
                          <th className="pb-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grnPayments.map((payment) => (
                          <tr key={payment.id} className={`border-b hover:bg-slate-50 ${payment.urgency === 'overdue' ? 'bg-red-50' : payment.urgency === 'critical' ? 'bg-orange-50' : ''} ${selectedGRNs.includes(payment.id) ? 'bg-blue-50' : ''}`}>
                            <td className="py-3 pr-2">
                              {payment.status !== 'paid' && (
                                <input
                                  type="checkbox"
                                  checked={selectedGRNs.includes(payment.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedGRNs([...selectedGRNs, payment.id]);
                                    } else {
                                      setSelectedGRNs(selectedGRNs.filter(id => id !== payment.id));
                                    }
                                  }}
                                  className="rounded"
                                />
                              )}
                            </td>
                            <td className="py-3 pr-4 font-mono text-sm">{payment.reference}</td>
                            <td className="py-3 pr-4">{payment.tax_number}</td>
                            <td className="py-3 pr-4">{formatCurrency(payment.amount)}</td>
                            <td className="py-3 pr-4 font-medium">{formatCurrency(payment.open_amount)}</td>
                            <td className="py-3 pr-4">{formatDate(payment.due_date)}</td>
                            <td className="py-3 pr-4">
                              {payment.latest_due_date ? (
                                <span className="text-red-600 font-medium">{formatDate(payment.latest_due_date)}</span>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedGRN(payment);
                                    setShowGRNModal(true);
                                  }}
                                >
                                  Set Date
                                </Button>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              <Badge className={
                                payment.urgency === 'overdue' ? 'bg-red-500 text-white' :
                                payment.urgency === 'critical' ? 'bg-orange-500 text-white' :
                                payment.urgency === 'urgent' ? 'bg-yellow-500 text-white' :
                                'bg-green-100 text-green-700'
                              }>
                                {payment.days_to_due} days
                              </Badge>
                            </td>
                            <td className="py-3 pr-4">
                              <Badge className={
                                payment.status === 'paid' ? 'bg-green-100 text-green-700' :
                                payment.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }>
                                {payment.status}
                              </Badge>
                            </td>
                            <td className="py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedGRN(payment);
                                    setShowGRNModal(true);
                                  }}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit Dates
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={async () => {
                                    try {
                                      await axios.post(`${API_URL}/vendor-ap/grn-payments/${payment.id}/mark-paid`, {}, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success("GRN payment marked as paid");
                                      fetchGRNPayments();
                                    } catch (error) {
                                      toast.error("Failed to mark as paid");
                                    }
                                  }}>
                                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                    Mark Paid
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* GRN Reminders Alert */}
            {grnPayments.filter(p => p.urgency === 'critical' || p.urgency === 'overdue').length > 0 && (
              <Card className="mt-4 border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertOctagon className="h-5 w-5" />
                    Urgent GRN Payments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {grnPayments
                      .filter(p => p.urgency === 'critical' || p.urgency === 'overdue')
                      .map(payment => (
                        <div key={payment.id} className="flex items-center justify-between p-2 bg-white rounded border border-red-200">
                          <div>
                            <span className="font-mono text-sm">{payment.reference}</span>
                            <span className="mx-2 text-slate-400">|</span>
                            <span className="font-medium">{formatCurrency(payment.open_amount)}</span>
                          </div>
                          <Badge className={payment.urgency === 'overdue' ? 'bg-red-500' : 'bg-orange-500'}>
                            {payment.urgency === 'overdue' ? `${Math.abs(payment.days_to_due)} days OVERDUE` : `${payment.days_to_due} days left`}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Payment Drafts Tab */}
          <TabsContent value="drafts">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Create Draft / Immediate Payment */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-blue-500" />
                    Payment Actions
                  </CardTitle>
                  <CardDescription>Create payment drafts or send immediate payments</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Immediate Payment Button */}
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => setShowImmediatePaymentModal(true)}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Immediate Payment
                    </Button>
                    
                    <div className="text-center text-sm text-slate-400">or</div>
                    
                    <p className="text-sm text-slate-500">
                      Go to <strong>GRN Customs</strong>, <strong>Pay Now</strong>, or <strong>Invoices</strong> tab to select items and push them to a payment draft.
                    </p>
                    
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="text-sm text-slate-500 mb-2">Payment Flow (acc@europacific.com):</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">1</span>
                          <span>Select items & create draft</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold">2</span>
                          <span>If &gt;€10K: Board approval required</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold">3</span>
                          <span>Email sent to acc@europacific.com</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold">4</span>
                          <span>Mark effected → Auto email to vendor</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Drafts List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Payment Drafts</CardTitle>
                      <CardDescription>Track payment processing status</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchPaymentDrafts}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {paymentDrafts.length === 0 ? (
                    <div className="text-center py-8">
                      <Send className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No payment drafts yet</p>
                      <p className="text-xs text-slate-400 mt-1">Select items from GRN, Pay Now, or Invoices to create drafts</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentDrafts.map((draft) => (
                        <div key={draft.id} className="p-4 bg-slate-50 rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className="font-medium">
                                {draft.type === 'immediate' ? 'Immediate' : 'Draft'} #{draft.id?.slice(-6)}
                              </span>
                              <Badge className={
                                draft.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                                draft.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' :
                                draft.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                draft.status === 'sent_to_accounting' ? 'bg-purple-100 text-purple-700' :
                                draft.status === 'effected' ? 'bg-green-100 text-green-700' :
                                draft.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100'
                              }>
                                {draft.status?.replace(/_/g, ' ')}
                              </Badge>
                              {draft.requires_approval && draft.status !== 'effected' && (
                                <Badge className="bg-orange-100 text-orange-700">
                                  &gt;€10K Approval
                                </Badge>
                              )}
                              {draft.source_type && (
                                <Badge variant="outline" className="text-xs">
                                  {draft.source_type.toUpperCase()}
                                </Badge>
                              )}
                            </div>
                            <span className="text-lg font-bold">{formatCurrency(draft.total_amount || draft.amount)}</span>
                          </div>
                          
                          {/* Immediate payment details */}
                          {draft.type === 'immediate' && (
                            <div className="text-sm text-slate-600 mb-2">
                              <span className="font-medium">{draft.vendor_name}</span>
                              {draft.notes && <p className="text-slate-500 mt-1">"{draft.notes}"</p>}
                            </div>
                          )}
                          
                          {/* Regular draft items */}
                          {draft.items && draft.items.length > 0 && (
                            <div className="text-sm text-slate-600 mb-2">
                              <span>{draft.items.length} item(s): </span>
                              <span className="text-slate-500">
                                {draft.items.slice(0, 3).map(i => i.vendor_name || i.reference || i.invoice_number).join(", ")}
                                {draft.items.length > 3 && ` +${draft.items.length - 3} more`}
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span>Created: {formatDate(draft.created_at)}</span>
                            {draft.payment_date && <span>Due: {formatDate(draft.payment_date)}</span>}
                            {draft.accounting_email && <span className="text-purple-600">→ {draft.accounting_email}</span>}
                          </div>
                          
                          {draft.status === 'draft' && (
                            <div className="mt-3 flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={async () => {
                                  try {
                                    const response = await axios.post(`${API_URL}/vendor-ap/payment-drafts/${draft.id}/submit`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success(response.data.message);
                                    fetchPaymentDrafts();
                                  } catch (error) {
                                    toast.error("Failed to submit draft");
                                  }
                                }}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Submit for Processing
                              </Button>
                            </div>
                          )}
                          {draft.status === 'sent_to_accounting' && (
                            <div className="mt-3 flex gap-2">
                              <Button 
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-600"
                                onClick={async () => {
                                  try {
                                    const response = await axios.post(`${API_URL}/vendor-ap/payment-drafts/${draft.id}/mark-effected`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success("Payment effected - auto emails will be sent to vendors");
                                    fetchPaymentDrafts();
                                    fetchInvoices();
                                  } catch (error) {
                                    toast.error("Failed to mark as effected");
                                  }
                                }}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark as Effected
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Board Members Management */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-500" />
                      Board Members (€10K+ Approvers)
                    </CardTitle>
                    <CardDescription>
                      Members who approve payments over €10,000
                    </CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setShowBoardMemberModal(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Member
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {boardMembers.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No board members configured</p>
                ) : (
                  <div className="space-y-2">
                    {boardMembers.map((member) => (
                      <div 
                        key={member.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          member.is_active ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${member.is_active ? "bg-green-500" : "bg-slate-400"}`} />
                          <div>
                            <div className="font-medium">{member.name}</div>
                            <div className="text-sm text-slate-500">
                              {member.email}
                              {member.phone && ` • ${member.phone}`}
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {member.role || "Board Member"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => toggleBoardMemberActive(member.id, member.is_active)}
                          >
                            {member.is_active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteBoardMember(member.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> Only active members will receive approval notifications for payments over €10,000. 
                    Notifications are sent via Email, WhatsApp, and SMS.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Vendor Modal */}
      <Dialog open={showVendorModal} onOpenChange={setShowVendorModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Vendor</DialogTitle>
            <DialogDescription>Enter vendor details and settings</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Vendor Name *</Label>
              <Input
                value={vendorForm.name}
                onChange={(e) => setVendorForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Company name"
              />
            </div>

            <div>
              <Label>Category</Label>
              <Select
                value={vendorForm.category}
                onValueChange={(v) => setVendorForm((f) => ({ ...f, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VENDOR_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Terms (days)</Label>
              <Input
                type="number"
                value={vendorForm.payment_terms_days}
                onChange={(e) => setVendorForm((f) => ({ ...f, payment_terms_days: parseInt(e.target.value) || 30 }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_critical"
                checked={vendorForm.is_critical}
                onChange={(e) => setVendorForm((f) => ({ ...f, is_critical: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="is_critical">Critical Vendor (hard to replace)</Label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_vital"
                checked={vendorForm.is_vital || false}
                onChange={(e) => setVendorForm((f) => ({ ...f, is_vital: e.target.checked }))}
                className="rounded border-orange-500"
              />
              <Label htmlFor="is_vital" className="text-orange-700">Vital Vendor (track closely)</Label>
            </div>

            {vendorForm.is_vital && (
              <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-orange-200">
                <div>
                  <Label className="text-sm">Max Outstanding (EUR)</Label>
                  <Input
                    type="number"
                    step="100"
                    placeholder="Alert if exceeds..."
                    value={vendorForm.vital_max_outstanding || ""}
                    onChange={(e) => setVendorForm((f) => ({ ...f, vital_max_outstanding: parseFloat(e.target.value) || null }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Max Days Overdue</Label>
                  <Input
                    type="number"
                    placeholder="Alert if exceeds..."
                    value={vendorForm.vital_max_days_overdue || ""}
                    onChange={(e) => setVendorForm((f) => ({ ...f, vital_max_days_overdue: parseInt(e.target.value) || null }))}
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Substitutability (0=easy, 3=hard)</Label>
              <Select
                value={vendorForm.substitutability.toString()}
                onValueChange={(v) => setVendorForm((f) => ({ ...f, substitutability: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 - Easy to replace</SelectItem>
                  <SelectItem value="1">1 - Some effort</SelectItem>
                  <SelectItem value="2">2 - Difficult</SelectItem>
                  <SelectItem value="3">3 - Very hard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Alternative Vendor Count</Label>
              <Input
                type="number"
                value={vendorForm.alt_vendor_count}
                onChange={(e) => setVendorForm((f) => ({ ...f, alt_vendor_count: parseInt(e.target.value) || 0 }))}
              />
            </div>

            <div>
              <Label>VAT Number</Label>
              <Input
                value={vendorForm.vat_number}
                onChange={(e) => setVendorForm((f) => ({ ...f, vat_number: e.target.value }))}
              />
            </div>

            <div>
              <Label>Contact Name</Label>
              <Input
                value={vendorForm.contact_name}
                onChange={(e) => setVendorForm((f) => ({ ...f, contact_name: e.target.value }))}
              />
            </div>

            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={vendorForm.contact_email}
                onChange={(e) => setVendorForm((f) => ({ ...f, contact_email: e.target.value }))}
              />
            </div>

            <div>
              <Label>Contact Phone</Label>
              <Input
                value={vendorForm.contact_phone}
                onChange={(e) => setVendorForm((f) => ({ ...f, contact_phone: e.target.value }))}
              />
            </div>

            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea
                value={vendorForm.address}
                onChange={(e) => setVendorForm((f) => ({ ...f, address: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={vendorForm.notes}
                onChange={(e) => setVendorForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateVendor} disabled={loading}>
              {loading ? "Creating..." : "Create Vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Invoice</DialogTitle>
            <DialogDescription>Record a vendor invoice</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Vendor *</Label>
              <Select
                value={invoiceForm.vendor_id}
                onValueChange={(v) => setInvoiceForm((f) => ({ ...f, vendor_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor..." />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Invoice Number *</Label>
              <Input
                value={invoiceForm.invoice_number}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_number: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoiceForm.amount}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={invoiceForm.currency}
                  onValueChange={(v) => setInvoiceForm((f) => ({ ...f, currency: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="CHF">CHF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invoice Date *</Label>
                <Input
                  type="date"
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, due_date: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label>Status</Label>
              <Select
                value={invoiceForm.status}
                onValueChange={(v) => setInvoiceForm((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="disputed">Disputed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateInvoice} disabled={loading}>
              {loading ? "Creating..." : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Contact Event Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Contact Event</DialogTitle>
            <DialogDescription>Record a call, email, or visit from a vendor</DialogDescription>
          </DialogHeader>

          {/* Email Drop Zone */}
          <div
            onDrop={handleContactEmailDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDraggingContact(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDraggingContact(false); }}
            className={`border-2 border-dashed rounded-lg p-3 transition-all cursor-pointer ${
              isDraggingContact 
                ? "border-blue-500 bg-blue-50" 
                : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
            } ${isProcessingEmail ? "opacity-50 pointer-events-none" : ""}`}
          >
            <div className="flex items-center justify-center gap-2 text-center">
              {isProcessingEmail ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-700">Processing email...</span>
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {isDraggingContact ? "Drop email to auto-fill" : "Drop .eml or .msg file to auto-fill form"}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4 py-2">
            {/* Vendor Field - Searchable with Free Text */}
            <div>
              <Label>Vendor *</Label>
              <Popover open={vendorSearchOpen} onOpenChange={setVendorSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={vendorSearchOpen}
                    className="w-full justify-between font-normal"
                  >
                    {contactForm.vendor_id 
                      ? vendors.find(v => v.id === contactForm.vendor_id)?.name 
                      : contactForm.vendor_name 
                        ? contactForm.vendor_name 
                        : "Search or enter vendor..."}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search vendors..." 
                      value={vendorSearchQuery}
                      onValueChange={(value) => {
                        setVendorSearchQuery(value);
                        // Allow free text entry
                        if (value && !vendors.some(v => v.name.toLowerCase() === value.toLowerCase())) {
                          setContactForm(f => ({ ...f, vendor_id: "", vendor_name: value }));
                        }
                      }}
                    />
                    <CommandList>
                      <CommandEmpty>
                        <div className="p-2 text-sm">
                          <p className="text-slate-500 mb-2">No vendor found</p>
                          {vendorSearchQuery && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full"
                              onClick={() => {
                                setContactForm(f => ({ 
                                  ...f, 
                                  vendor_id: "", 
                                  vendor_name: vendorSearchQuery,
                                  pic_name: "",
                                  pic_email: "",
                                  pic_phone: ""
                                }));
                                setSelectedVendorContacts([]);
                                setVendorSearchOpen(false);
                              }}
                            >
                              <UserPlus className="h-4 w-4 mr-2" />
                              Use "{vendorSearchQuery}" as new vendor
                            </Button>
                          )}
                        </div>
                      </CommandEmpty>
                      <CommandGroup heading="Vendors">
                        {vendors
                          .filter(v => v.name.toLowerCase().includes(vendorSearchQuery.toLowerCase()))
                          .slice(0, 10)
                          .map((vendor) => (
                            <CommandItem
                              key={vendor.id}
                              value={vendor.name}
                              onSelect={() => {
                                setContactForm(f => ({ 
                                  ...f, 
                                  vendor_id: vendor.id, 
                                  vendor_name: "",
                                  pic_name: "",
                                  pic_email: "",
                                  pic_phone: ""
                                }));
                                // Load vendor contacts for PIC selection
                                setSelectedVendorContacts(vendor.contacts || []);
                                setVendorSearchOpen(false);
                                setVendorSearchQuery("");
                              }}
                            >
                              <Building2 className="mr-2 h-4 w-4 text-slate-400" />
                              <div className="flex-1">
                                <span className="font-medium">{vendor.name}</span>
                                {vendor.category && (
                                  <span className="ml-2 text-xs text-slate-400 capitalize">{vendor.category}</span>
                                )}
                              </div>
                              {contactForm.vendor_id === vendor.id && (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              )}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {contactForm.vendor_name && !contactForm.vendor_id && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertOctagon className="h-3 w-3" />
                  New vendor (not in database)
                </p>
              )}
            </div>

            {/* PIC (Person In Charge) Field */}
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                Person In Charge (PIC)
              </Label>
              
              {selectedVendorContacts.length > 0 ? (
                <div className="space-y-2">
                  <Select
                    value={contactForm.pic_email || "custom"}
                    onValueChange={(v) => {
                      if (v === "custom") {
                        setContactForm(f => ({ ...f, pic_name: "", pic_email: "", pic_phone: "" }));
                      } else {
                        const contact = selectedVendorContacts.find(c => c.email === v);
                        if (contact) {
                          setContactForm(f => ({
                            ...f,
                            pic_name: contact.name || "",
                            pic_email: contact.email || "",
                            pic_phone: contact.phone || ""
                          }));
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select contact..." />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedVendorContacts.map((contact, idx) => (
                        <SelectItem key={idx} value={contact.email || `contact-${idx}`}>
                          <span className="flex items-center gap-2">
                            <User className="h-3 w-3" />
                            {contact.name || contact.email}
                            {contact.role && <span className="text-xs text-slate-400">({contact.role})</span>}
                          </span>
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">
                        <span className="flex items-center gap-2 text-blue-600">
                          <UserPlus className="h-3 w-3" />
                          Enter custom contact
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <p className="text-xs text-slate-500">Enter contact details manually</p>
              )}
              
              <div className="grid grid-cols-1 gap-2">
                <Input
                  placeholder="Contact name"
                  value={contactForm.pic_name}
                  onChange={(e) => setContactForm(f => ({ ...f, pic_name: e.target.value }))}
                  className="bg-white"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Email"
                    type="email"
                    value={contactForm.pic_email}
                    onChange={(e) => setContactForm(f => ({ ...f, pic_email: e.target.value }))}
                    className="bg-white"
                  />
                  <Input
                    placeholder="Phone"
                    value={contactForm.pic_phone}
                    onChange={(e) => setContactForm(f => ({ ...f, pic_phone: e.target.value }))}
                    className="bg-white"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Channel</Label>
                <Select
                  value={contactForm.channel}
                  onValueChange={(v) => setContactForm((f) => ({ ...f, channel: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="visit">In-Person Visit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Direction</Label>
                <Select
                  value={contactForm.direction}
                  onValueChange={(v) => setContactForm((f) => ({ ...f, direction: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbound">Inbound (from vendor)</SelectItem>
                    <SelectItem value="outbound">Outbound (to vendor)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Topic</Label>
                <Select
                  value={contactForm.topic}
                  onValueChange={(v) => setContactForm((f) => ({ ...f, topic: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment_request">Payment Request</SelectItem>
                    <SelectItem value="reminder">Payment Reminder</SelectItem>
                    <SelectItem value="dispute">Dispute</SelectItem>
                    <SelectItem value="inquiry">General Inquiry</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Urgency</Label>
                <Select
                  value={contactForm.urgency}
                  onValueChange={(v) => setContactForm((f) => ({ ...f, urgency: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={contactForm.notes}
                onChange={(e) => setContactForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
                placeholder="What was discussed..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateContactEvent} disabled={loading}>
              {loading ? "Logging..." : "Log Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GRN Edit Modal */}
      <Dialog open={showGRNModal} onOpenChange={setShowGRNModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit GRN Payment Dates</DialogTitle>
            <DialogDescription>
              Set OTP letter date and latest due date for customs payment
            </DialogDescription>
          </DialogHeader>

          {selectedGRN && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-500">Reference</div>
                <div className="font-mono font-medium">{selectedGRN.reference}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-500">Amount</div>
                  <div className="font-medium">{formatCurrency(selectedGRN.amount)}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Open Amount</div>
                  <div className="font-medium">{formatCurrency(selectedGRN.open_amount)}</div>
                </div>
              </div>

              <div>
                <div className="text-sm text-slate-500">Original Due Date</div>
                <div className="font-medium">{formatDate(selectedGRN.due_date)}</div>
              </div>

              <div>
                <Label>OTP Letter Received Date</Label>
                <Input
                  type="date"
                  defaultValue={selectedGRN.otp_letter_date ? new Date(selectedGRN.otp_letter_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setSelectedGRN(prev => ({ ...prev, otp_letter_date: e.target.value }))}
                />
                <p className="text-xs text-slate-500 mt-1">When OTP bank received customs letter</p>
              </div>

              <div>
                <Label className="text-red-600">Latest Due Date (URGENT)</Label>
                <Input
                  type="date"
                  defaultValue={selectedGRN.latest_due_date ? new Date(selectedGRN.latest_due_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setSelectedGRN(prev => ({ ...prev, latest_due_date: e.target.value }))}
                />
                <p className="text-xs text-red-500 mt-1">Final hard deadline from customs</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGRNModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                if (!selectedGRN) return;
                try {
                  await axios.patch(`${API_URL}/vendor-ap/grn-payments/${selectedGRN.id}`, {
                    otp_letter_date: selectedGRN.otp_letter_date ? new Date(selectedGRN.otp_letter_date).toISOString() : null,
                    latest_due_date: selectedGRN.latest_due_date ? new Date(selectedGRN.latest_due_date).toISOString() : null,
                  }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success("GRN dates updated");
                  setShowGRNModal(false);
                  fetchGRNPayments();
                } catch (error) {
                  toast.error("Failed to update GRN");
                }
              }}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Immediate Payment Modal */}
      <Dialog open={showImmediatePaymentModal} onOpenChange={setShowImmediatePaymentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" />
              Immediate Payment
            </DialogTitle>
            <DialogDescription>
              Quick payment instruction to acc@europacific.com
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Vendor Selection */}
            <div>
              <Label>Select Vendor (Optional)</Label>
              <Select
                value={immediatePaymentForm.vendor_id}
                onValueChange={(v) => {
                  if (v) {
                    fetchVendorSOA(v);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor to see SOA..." />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* SOA Info */}
            {vendorSOA && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">Total SOA for {vendorSOA.vendor_name}</span>
                  <span className="text-lg font-bold text-blue-800">{formatCurrency(vendorSOA.total_soa)}</span>
                </div>
                <div className="text-xs text-blue-600 mt-1">{vendorSOA.open_invoices_count} open invoices</div>
              </div>
            )}

            {/* Or Manual Vendor Name */}
            <div>
              <Label>Vendor Name *</Label>
              <Input
                value={immediatePaymentForm.vendor_name}
                onChange={(e) => setImmediatePaymentForm(prev => ({ ...prev, vendor_name: e.target.value }))}
                placeholder="e.g., CARGO PARTNER"
              />
            </div>

            {/* Amount */}
            <div>
              <Label>Amount (EUR) *</Label>
              <Input
                type="number"
                step="0.01"
                value={immediatePaymentForm.amount || ""}
                onChange={(e) => setImmediatePaymentForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                placeholder="200.00"
              />
              {immediatePaymentForm.amount >= 10000 && (
                <p className="text-xs text-orange-600 mt-1">⚠️ Payments over €10,000 require board approval</p>
              )}
            </div>

            {/* Payment Type */}
            <div>
              <Label>Payment Type</Label>
              <Select
                value={immediatePaymentForm.payment_type}
                onValueChange={(v) => setImmediatePaymentForm(prev => ({ ...prev, payment_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="partial">Partial Payment</SelectItem>
                  <SelectItem value="full">Full Payment</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes / Instructions */}
            <div>
              <Label>Payment Instructions *</Label>
              <Textarea
                value={immediatePaymentForm.notes}
                onChange={(e) => setImmediatePaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g., Pay 200€ as partial payment to total SOA"
                rows={3}
              />
              <p className="text-xs text-slate-500 mt-1">This note will be sent to accounting</p>
            </div>

            {/* Total SOA Reference */}
            {vendorSOA && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-500">Reference for Accounting:</div>
                <div className="font-medium text-slate-700">
                  {immediatePaymentForm.vendor_name} - {immediatePaymentForm.payment_type === 'partial' ? 'Partial' : 'Full'} payment of {formatCurrency(immediatePaymentForm.amount)} 
                  {vendorSOA.total_soa > 0 && ` (Total SOA: ${formatCurrency(vendorSOA.total_soa)})`}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowImmediatePaymentModal(false);
                setVendorSOA(null);
                setImmediatePaymentForm({
                  vendor_id: "",
                  vendor_name: "",
                  amount: 0,
                  payment_type: "partial",
                  notes: "",
                  total_soa: 0
                });
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleCreateImmediatePayment}
              disabled={loading || !immediatePaymentForm.vendor_name || !immediatePaymentForm.amount}
            >
              {loading ? "Sending..." : "Send to Accounting"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Board Member Modal */}
      <Dialog open={showBoardMemberModal} onOpenChange={setShowBoardMemberModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-purple-600" />
              Add Board Member
            </DialogTitle>
            <DialogDescription>
              Add a board member for payment approval (€10K+)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={boardMemberForm.name}
                onChange={(e) => setBoardMemberForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={boardMemberForm.email}
                onChange={(e) => setBoardMemberForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@company.com"
              />
            </div>

            <div>
              <Label>Phone (for SMS)</Label>
              <Input
                value={boardMemberForm.phone}
                onChange={(e) => setBoardMemberForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+386 40 123 456"
              />
            </div>

            <div>
              <Label>WhatsApp Number</Label>
              <Input
                value={boardMemberForm.whatsapp}
                onChange={(e) => setBoardMemberForm(prev => ({ ...prev, whatsapp: e.target.value }))}
                placeholder="+386 40 123 456"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty to use phone number for WhatsApp</p>
            </div>

            <div>
              <Label>Role</Label>
              <Select
                value={boardMemberForm.role}
                onValueChange={(v) => setBoardMemberForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Board Member">Board Member</SelectItem>
                  <SelectItem value="Director">Director</SelectItem>
                  <SelectItem value="CFO">CFO</SelectItem>
                  <SelectItem value="CEO">CEO</SelectItem>
                  <SelectItem value="Test Approver">Test Approver</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={boardMemberForm.is_active}
                onChange={(e) => setBoardMemberForm(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="is_active">Active (receives notifications)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBoardMemberModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleAddBoardMember}
              disabled={!boardMemberForm.name || !boardMemberForm.email}
            >
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excluded Liabilities Panel */}
      <Dialog open={showExclusionsPanel} onOpenChange={setShowExclusionsPanel}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EyeOff className="h-5 w-5 text-purple-600" />
              Excluded Liabilities / Pending Write-offs
            </DialogTitle>
            <DialogDescription>
              These items are excluded from cash flow projections but remain as accounting liabilities.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-700">
                  €{(cashflowExclusions?.summary?.total_excluded_amount || 0).toLocaleString()}
                </p>
                <p className="text-sm text-purple-600">Total Excluded</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{cashflowExclusions?.summary?.total_count || 0}</p>
                <p className="text-sm text-slate-600">Items</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-700">
                  €{(cashflowExclusions?.summary?.probability_weighted_risk || 0).toLocaleString()}
                </p>
                <p className="text-sm text-orange-600">Risk-Weighted Exposure</p>
              </div>
            </div>

            {/* By Reason Breakdown */}
            {cashflowExclusions?.summary?.by_reason && Object.keys(cashflowExclusions.summary.by_reason).length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium mb-2">By Reason:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(cashflowExclusions.summary.by_reason).map(([reason, data]) => (
                    <Badge key={reason} variant="outline" className="bg-white">
                      {EXCLUSION_REASONS.find(r => r.value === reason)?.label || reason}: {data.count} (€{data.amount?.toLocaleString()})
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* List of Excluded Items */}
            <ScrollArea className="h-[350px] border rounded-lg">
              {cashflowExclusions?.excluded_invoices?.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No items currently excluded from cash flow</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left border-b">
                      <th className="p-3">Vendor</th>
                      <th className="p-3">Invoice</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Reason</th>
                      <th className="p-3">Resolution</th>
                      <th className="p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashflowExclusions?.excluded_invoices?.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-slate-50">
                        <td className="p-3 font-medium">{item.vendor_name}</td>
                        <td className="p-3">{item.invoice_number}</td>
                        <td className="p-3 font-medium">€{item.amount_eur?.toLocaleString()}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">
                            {EXCLUSION_REASONS.find(r => r.value === item.exclusion_reason)?.label || item.exclusion_reason}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs">
                          {item.expected_resolution_date 
                            ? formatDate(item.expected_resolution_date)
                            : <span className="text-slate-400">Not set</span>
                          }
                        </td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowExclusionsPanel(false);
                              openExclusionModal({
                                id: item.id,
                                invoice_number: item.invoice_number,
                                vendor_name: item.vendor_name,
                                amount_eur: item.amount_eur,
                                include_in_cashflow: false,
                                cashflow_exclusion_reason: item.exclusion_reason,
                                cashflow_exclusion_comment: item.exclusion_comment,
                                expected_resolution_date: item.expected_resolution_date,
                                exclusion_probability: item.exclusion_probability,
                                adjusted_cash_amount: item.adjusted_cash_amount
                              }, true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Re-include
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExclusionsPanel(false)}>
              Close
            </Button>
            <Button onClick={fetchCashflowExclusions}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Flow Exclusion Modal */}
      <Dialog open={showExclusionModal} onOpenChange={setShowExclusionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {exclusionForm.include_in_cashflow ? (
                <>
                  <Eye className="h-5 w-5 text-green-600" />
                  Re-include in Cash Flow Forecast
                </>
              ) : (
                <>
                  <EyeOff className="h-5 w-5 text-purple-600" />
                  Exclude from Cash Flow Forecast
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {exclusionForm.include_in_cashflow 
                ? "This invoice will be included in cash flow projections again."
                : "Excluded items won't appear in cash flow projections but remain as liabilities."
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Invoice Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{exclusionForm.vendor_name}</p>
                  <p className="text-sm text-slate-500">Invoice: {exclusionForm.invoice_number}</p>
                </div>
                <p className="text-lg font-bold">€{exclusionForm.amount?.toLocaleString()}</p>
              </div>
            </div>

            {/* Only show exclusion fields when excluding */}
            {!exclusionForm.include_in_cashflow && (
              <>
                {/* Reason */}
                <div>
                  <Label className="text-sm font-medium">Exclusion Reason *</Label>
                  <Select
                    value={exclusionForm.cashflow_exclusion_reason}
                    onValueChange={(v) => setExclusionForm(prev => ({ ...prev, cashflow_exclusion_reason: v }))}
                  >
                    <SelectTrigger data-testid="exclusion-reason-select">
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {EXCLUSION_REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Comment */}
                <div>
                  <Label className="text-sm font-medium">Comment *</Label>
                  <Textarea
                    value={exclusionForm.cashflow_exclusion_comment}
                    onChange={(e) => setExclusionForm(prev => ({ ...prev, cashflow_exclusion_comment: e.target.value }))}
                    placeholder="Explain why this invoice is excluded from cash flow forecast..."
                    rows={3}
                    data-testid="exclusion-comment"
                  />
                </div>

                {/* Expected Resolution Date */}
                <div>
                  <Label className="text-sm font-medium">Expected Resolution Date</Label>
                  <Input
                    type="date"
                    value={exclusionForm.expected_resolution_date}
                    onChange={(e) => setExclusionForm(prev => ({ ...prev, expected_resolution_date: e.target.value }))}
                    data-testid="exclusion-resolution-date"
                  />
                  <p className="text-xs text-slate-500 mt-1">When do you expect this to be resolved?</p>
                </div>

                {/* Partial Exclusion Option */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="use_partial"
                      checked={exclusionForm.use_partial_exclusion}
                      onChange={(e) => setExclusionForm(prev => ({ 
                        ...prev, 
                        use_partial_exclusion: e.target.checked,
                        adjusted_cash_amount: e.target.checked ? prev.amount * 0.5 : null
                      }))}
                      className="rounded"
                    />
                    <Label htmlFor="use_partial" className="font-medium">Partial exclusion (adjust expected payment)</Label>
                  </div>
                  
                  {exclusionForm.use_partial_exclusion && (
                    <div className="pl-6 space-y-2">
                      <Label className="text-sm">Expected Cash Payment (€)</Label>
                      <Input
                        type="number"
                        value={exclusionForm.adjusted_cash_amount || 0}
                        onChange={(e) => setExclusionForm(prev => ({ ...prev, adjusted_cash_amount: parseFloat(e.target.value) || 0 }))}
                        min={0}
                        max={exclusionForm.amount}
                        data-testid="adjusted-cash-amount"
                      />
                      <p className="text-xs text-slate-500">
                        Full amount: €{exclusionForm.amount?.toLocaleString()} | 
                        Excluded: €{(exclusionForm.amount - (exclusionForm.adjusted_cash_amount || 0)).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Probability */}
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    Exclusion Probability: {exclusionForm.exclusion_probability}%
                  </Label>
                  <input
                    type="range"
                    min={50}
                    max={100}
                    value={exclusionForm.exclusion_probability}
                    onChange={(e) => setExclusionForm(prev => ({ ...prev, exclusion_probability: parseInt(e.target.value) }))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>50% (Uncertain)</span>
                    <span>100% (Certain)</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExclusionModal(false)}>
              Cancel
            </Button>
            <Button 
              className={exclusionForm.include_in_cashflow ? "bg-green-600 hover:bg-green-700" : "bg-purple-600 hover:bg-purple-700"}
              onClick={handleSaveExclusion}
              disabled={loading || (!exclusionForm.include_in_cashflow && (!exclusionForm.cashflow_exclusion_reason || !exclusionForm.cashflow_exclusion_comment))}
              data-testid="save-exclusion-btn"
            >
              {loading ? "Saving..." : (exclusionForm.include_in_cashflow ? "Re-include" : "Exclude from Cash Flow")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
